import {
  intranetAuthorGrantListSchema,
  intranetAuthorGrantSchema,
  type CreateIntranetAuthorGrantRequest,
  type IntranetAuthorGrantListQuery,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiFetch } from '@/lib/api';
import { toSearchParams } from '@/lib/format';

export const authorGrantsQueryKey = ['intranet', 'author-grants'] as const;

const removedSchema = z.object({ ok: z.literal(true) });

export function useAuthorGrants(
  query: IntranetAuthorGrantListQuery,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [...authorGrantsQueryKey, query],
    enabled: options?.enabled ?? true,
    queryFn: () =>
      apiFetch(
        `/intranet/author-grants${toSearchParams({
          q: query.q,
          sortBy: query.sortBy,
          sortOrder: query.sortOrder,
        })}`,
        intranetAuthorGrantListSchema,
      ),
  });
}

export function useCreateAuthorGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateIntranetAuthorGrantRequest) =>
      apiFetch('/intranet/author-grants', intranetAuthorGrantSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authorGrantsQueryKey });
    },
  });
}

export function useRemoveAuthorGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/intranet/author-grants/${id}`, removedSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authorGrantsQueryKey });
    },
  });
}
